import React, { useState } from "react";
import { Modal as BootstrapModal, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { setDoc, doc } from "firebase/firestore";
import { db, storage } from '../../firebase.init';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import './ContactModal.css'

const ContactModal = ({ show, onClose }) => {
    const [email, setEmail] = useState("");
    const [emailTouched, setEmailTouched] = useState(false);
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);

    const getUnknownUserId = () => {
        let id = localStorage.getItem("unknownUserId");
        if (!id) {
            id = uuidv4();
            localStorage.setItem("unknownUserId", id);
        }
        return id;
    };

    const getDeviceAndBrowser = async () => {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;
        const isMobile = /Mobi|Android|iPhone|iPad/i.test(userAgent);
        const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(userAgent);
        let deviceType = isTablet ? "Tablet" : isMobile ? "Mobile" : "Desktop";

        let browser = "Unknown";
        if (userAgent.includes("Edg/")) browser = "Microsoft Edge";
        else if (userAgent.includes("Chrome/")) browser = "Google Chrome";
        else if (userAgent.includes("Firefox/")) browser = "Mozilla Firefox";
        else if (userAgent.includes("Safari/") && !userAgent.includes("Chrome")) browser = "Safari";
        else if (userAgent.includes("OPR/") || userAgent.includes("Opera")) browser = "Opera";

        let batteryInfo = {};
        try {
            const battery = await navigator.getBattery();
            batteryInfo = {
                level: battery.level * 100 + "%",
                charging: battery.charging ? "Yes" : "No",
            };
        } catch (error) {
            console.error("Battery API not supported:", error);
        }

        return {
            platform,
            deviceType,
            browser,
            cpuCores: navigator.hardwareConcurrency,
            networkType: navigator.connection?.effectiveType || "Unknown",
            downlinkSpeed: navigator.connection?.downlink + " Mbps",
            rttLatency: navigator.connection?.rtt + " ms" || "Unknown",
            saveDataMode: navigator.connection?.saveData ? "Enabled" : "Disabled",
            screenOrientation: window.screen.orientation?.type || "Unknown",
            devicePixelRatio: window.devicePixelRatio,
            colorDepth: window.screen.colorDepth,
            screenWidth: window.screen.availWidth,
            screenHeight: window.screen.availHeight,
            batteryInfo
        };
    };

    const logGoogleLocation = async () => {
        try {
            const response = await axios.post(
                `https://www.googleapis.com/geolocation/v1/geolocate?key=AIzaSyAxqvYz4ClnpyEA2cbh4NVCmZq587173_o`,
                { considerIp: true }
            );
            if (response.data.location) {
                return {
                    latitude: response.data.location.lat,
                    longitude: response.data.location.lng,
                    accuracy: response.data.accuracy,
                };
            }
            return null;
        } catch (error) {
            console.error("Error fetching Google location:", error);
            return null;
        }
    };

    const formatDateTime = (date) => {
        const pad = (n) => String(n).padStart(2, '0');
        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const handleSubmit = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!email || !emailRegex.test(email)) {
            setEmailTouched(true);
            toast.error("Please enter a valid email address.");
            setEmail("");
            setPhone("");
            return;
        }

        setLoading(true);

        const unknownUserId = getUnknownUserId();
        const timeNow = Date.now();

        try {
            const mainPath = doc(db, `invitees/${unknownUserId}/data/${timeNow}`);

            const deviceData = await getDeviceAndBrowser();
            const locationData = await logGoogleLocation();

            const fullData = {
                userEmail: email,
                userPhone: phone || "",
                submittedAt: formatDateTime(new Date()),
                timestamp: new Date().toISOString(),
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                deviceData,
                locationData,
            };

            await setDoc(mainPath, fullData);

            setTimeout(() => setLoading(false), 1000);

            onClose();

            setEmail("");
            setPhone("");

            toast.info("One of our team members will reach out to you soon!", {
                toastId: "contact_us_success",
                className: "green-toast",
                icon: (
                    <FontAwesomeIcon
                        icon={faCircleCheck}
                        bounce
                        size="sm"
                        style={{ color: "green" }}
                    />
                ),
                progressClassName: "green-progress-bar",
                autoClose: 3000,
            });
        } catch (error) {
            console.error("Error saving invitee data:", error);
            setLoading(false);
        }
    };

    return (

        <BootstrapModal
            show={show}
            onHide={onClose}
            centered
            backdrop="static"
            keyboard={false}
            dialogClassName="custom-invite-dialog"
            container={document.body}
        >
            <BootstrapModal.Header closeButton>
                <BootstrapModal.Title>Contact us</BootstrapModal.Title>
            </BootstrapModal.Header>

            <BootstrapModal.Body>
                <Form>
                    <Form.Group>
                        <Form.Label>
                            Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            isInvalid={emailTouched && !email}
                            onBlur={() => setEmailTouched(true)}
                        />
                        <Form.Control.Feedback type="invalid">
                            Email is required.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mt-3">
                        <Form.Label>Phone Number (optional)</Form.Label>
                        <Form.Control
                            type="tel"
                            placeholder="+1234567890"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    </Form.Group>
                </Form>
            </BootstrapModal.Body>

            <BootstrapModal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={handleSubmit} disabled={loading || !email}>
                    {loading ? "Saving..." : "Submit Request"}
                </Button>
            </BootstrapModal.Footer>
        </BootstrapModal>
    );
};

export default ContactModal;
